import { useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Truck,
  Users,
  Flower2,
  Package,
  BadgePercent,
  Boxes,
  Receipt,
  ScanLine,
  FileMinus,
  Percent,
  ClipboardList,
  BarChart3,
  Activity,
  ShieldUser,
  Settings,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasMenuAccess } from "@/constants/menus";

const NAV = [
  { to: "/", key: "dashboard", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/suppliers", key: "suppliers", label: "Proveedores", icon: Truck, testid: "nav-suppliers" },
  { to: "/customers", key: "customers", label: "Clientes", icon: Users, testid: "nav-customers" },
  { to: "/flower-types", key: "flower-types", label: "Tipos de Flores", icon: Flower2, testid: "nav-flower-types" },
  { to: "/products", key: "products", label: "Productos", icon: Package, testid: "nav-products" },
  { to: "/promotions", key: "promotions", label: "Promociones", icon: BadgePercent, testid: "nav-promotions" },
  { to: "/services", key: "services", label: "Servicios", icon: Flower2, testid: "nav-services" },
  { to: "/kardex", key: "kardex", label: "Kardex", icon: Boxes, testid: "nav-kardex" },
  { to: "/pos", key: "pos", label: "POS", icon: ScanLine, testid: "nav-pos" },
  { to: "/invoices", key: "invoices", label: "Facturas", icon: Receipt, testid: "nav-invoices" },
  { to: "/credit-notes", key: "credit-notes", label: "Notas de Crédito", icon: FileMinus, testid: "nav-credit-notes" },
  { to: "/retentions", key: "retentions", label: "Retenciones", icon: Percent, testid: "nav-retentions" },
  { to: "/remission-guides", key: "remission-guides", label: "Guías de Remisión", icon: ClipboardList, testid: "nav-remission-guides" },
  { to: "/reports", key: "reports", label: "Reportes", icon: BarChart3, testid: "nav-reports" },
  { to: "/sri-diagnostics", key: "sri-diagnostics", label: "Diagnóstico SRI", icon: Activity, testid: "nav-sri", roles: ["admin"] },
  { to: "/users", key: "users", label: "Usuarios", icon: ShieldUser, testid: "nav-users", roles: ["admin"] },
  { to: "/settings", key: "settings", label: "Ajustes", icon: Settings, testid: "nav-settings", roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleNav = useMemo(
    () => NAV
      .filter((n) => !n.roles || n.roles.includes(user?.role))
      .filter((n) => hasMenuAccess(user, n.key)),
    [user],
  );

  return (
    <div className="app-shell">
      <aside
        data-testid="sidebar"
        className="w-64 shrink-0 border-r border-[#E2E0D8] bg-[#FAFAF8] flex flex-col"
      >
        <div className="px-6 pt-7 pb-5 border-b border-[#E2E0D8]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#2A3F35] grid place-items-center">
              <Flower2 className="w-5 h-5 text-[#E4D5C7]" strokeWidth={1.7} />
            </div>
            <div>
              <p className="font-heading text-lg font-extrabold leading-none">Floristería</p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A9A92] mt-1">ERP · SRI EC</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-[#E2E0D8]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#E4D5C7] grid place-items-center text-[#5C2F1E] font-bold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p data-testid="current-user-name" className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">{user?.role}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center justify-center gap-2 text-sm text-[#5C6B64] hover:text-[#A44A4A] transition-colors py-2"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
